/**
 * graft/A5: structured page context for the Search agent dock.
 *
 * The dock's free-text snapshot (path/title/heading) tells the agent WHERE
 * the user is; this module tells it WHAT is open in terms its tools can act
 * on: the SPA route parsed back into the search service's entity vocabulary
 * (the same mapping getEntityLink builds forwards — this is that table in
 * reverse). With "open_record: bill 9001" in the context block the agent can
 * call get_record("bill", 9001) about the very record on screen.
 *
 * VOCABULARY: kinds are the server's spec slugs (app/entities/specs/*.yaml
 * in BTNet-Search): bill, purchase-order, change-order, customer-invoice,
 * jobsite, daily-log, schedule-item, document, sub, contact, … — the exact
 * strings /v1/record/{kind}/{id} and aggregate's entity_type accept.
 *
 * PARSING RULES:
 *  - Patterns match path SEGMENTS anywhere in the pathname: full-page routes
 *    carry portal prefixes ((/Admin|/Owner|/Subs), the /app basename) and
 *    the modal drawer APPENDS its route to the current page's URL (the
 *    /SearchResult prefix pattern), so anchoring to the start would miss it.
 *  - The LAST match in the path wins: a modal open over a detail page
 *    (…/Job/411/…/SearchResult/Bill/9001/411) is what the user is looking at.
 *  - Honest fallback: an unrecognized route yields null and the dock sends
 *    exactly the text-only context it always has.
 */

export interface IOpenRecordContext {
    /** Server entity slug (get_record/aggregate vocabulary). */
    kind: string;
    id: string;
    /** Owning job when the route carries one (jobsite id). */
    jobId?: string;
}

export interface IOpenListContext {
    /** Server entity slug, pluralized by the caller if desired — kept singular. */
    kind: string;
    /** Job filter when the URL carries one (?jobId= / ?jobsiteId=). */
    jobId?: string;
}

interface IRecordPattern {
    /** SPA route segment(s) -> capture(1)=id, optional capture(2)=jobId. */
    re: RegExp;
    kind: string;
    /** Job detail: the first capture IS the job id. */
    idIsJob?: boolean;
}

// Ordered table of detail-route shapes (routes.ts is the forward source of
// truth; segment names verified against its `details:` templates).
const RECORD_PATTERNS: IRecordPattern[] = [
    { re: /\/Bill\/(\d+)\/(\d+)/g, kind: "bill" },
    { re: /\/PurchaseOrder\/(\d+)\/(\d+)/g, kind: "purchase-order" },
    // Legacy modal (/ChangeOrder/:id/:jobId) and new full page
    // (/ChangeOrders/:id/:jobId/Details).
    { re: /\/ChangeOrders?\/(\d+)\/(\d+)/g, kind: "change-order" },
    { re: /\/OwnerInvoice\/(\d+)\/(\d+)/g, kind: "customer-invoice" },
    { re: /\/DailyLog\/(\d+)\/(\d+)/g, kind: "daily-log" },
    { re: /\/Schedule\/(\d+)\/(\d+)/g, kind: "schedule-item" },
    { re: /\/Warranty\/(\d+)\/(\d+)/g, kind: "warranty" },
    { re: /\/Selection\/(\d+)\/(\d+)/g, kind: "selection" },
    { re: /\/Allowance\/(\d+)\/(\d+)/g, kind: "allowance" },
    { re: /\/Deposit\/(\d+)\/(\d+)/g, kind: "deposit" },
    { re: /\/CreditMemos\/(\d+)\/(\d+)/g, kind: "credit-memo" },
    { re: /\/RFI\/(\d+)\/(\d+)/g, kind: "rfi" },
    { re: /\/Submittal\/(\d+)\/(\d+)/g, kind: "submittal" },
    // Media properties: /Media/Properties/:docInstanceId/:jobId/:mediaType/…
    // Only mediaType 1 (Document) maps to a KG entity; photos/videos have no
    // spec, so they fall through to the text-only context (honest fallback).
    { re: /\/Media\/Properties\/(\d+)\/(\d+)\/1\b/g, kind: "document" },
    { re: /\/SubVendor\/(\d+)/g, kind: "sub" },
    { re: /\/Contact\/(\d+)/g, kind: "contact" },
    { re: /\/Lead\/(\d+)/g, kind: "lead" },
    // Job detail: /Job/:jobId/:defaultTab/… — the job IS the record.
    { re: /\/Job\/(\d+)/g, kind: "jobsite", idIsJob: true },
];

// List pages: trailing path segment -> entity slug. Deliberately trailing-
// anchored (a list is a page, never a modal suffix with more segments after).
const LIST_PATTERNS: { re: RegExp; kind: string }[] = [
    { re: /\/Bills(?:\/InReview)?\/?$/, kind: "bill" },
    { re: /\/BillsInbox\/?$/, kind: "bill" },
    { re: /\/PurchaseOrders\/?$/, kind: "purchase-order" },
    { re: /\/ChangeOrders\/?$/, kind: "change-order" },
    { re: /\/OwnerInvoices\/?$/, kind: "customer-invoice" },
    { re: /\/DailyLogs\/?$/, kind: "daily-log" },
    { re: /\/Submittals\/?$/, kind: "submittal" },
    { re: /\/Warranties\/?$/, kind: "warranty" },
    { re: /\/Leads\/?$/, kind: "lead" },
];

/** Parse the OPEN RECORD from an SPA pathname; null when unrecognized. */
export function parseOpenRecord(pathname: string): IOpenRecordContext | null {
    let best: { index: number; ctx: IOpenRecordContext } | null = null;
    for (const pattern of RECORD_PATTERNS) {
        pattern.re.lastIndex = 0;
        for (const m of pathname.matchAll(pattern.re)) {
            const index = m.index ?? 0;
            if (best !== null && index <= best.index) continue;
            const ctx: IOpenRecordContext = pattern.idIsJob
                ? { kind: pattern.kind, id: m[1], jobId: m[1] }
                : { kind: pattern.kind, id: m[1], ...(m[2] ? { jobId: m[2] } : {}) };
            best = { index, ctx };
        }
    }
    return best?.ctx ?? null;
}

/** Parse an OPEN LIST page (optionally job-filtered via query); null when not a known list. */
export function parseOpenList(pathname: string, search: string = ""): IOpenListContext | null {
    for (const pattern of LIST_PATTERNS) {
        if (pattern.re.test(pathname)) {
            let jobId: string | undefined;
            try {
                const params = new URLSearchParams(search);
                jobId =
                    params.get("jobId") ??
                    params.get("jobsiteId") ??
                    params.get("jobID") ??
                    undefined;
            } catch {
                // malformed query string: list context still useful without it
            }
            return { kind: pattern.kind, ...(jobId ? { jobId } : {}) };
        }
    }
    return null;
}

/**
 * The structured context lines the dock appends to its free-text snapshot.
 * Record wins over list (a modal over a list page is the thing in focus);
 * unrecognized routes contribute nothing — the caller degrades to text-only.
 */
export function describeOpenContext(
    pathname: string,
    search: string = "",
    title?: string,
): string[] {
    const record = parseOpenRecord(pathname);
    if (record) {
        const suffix = title ? ` — ${title}` : "";
        const jobNote = record.jobId && record.kind !== "jobsite" ? ` (job: ${record.jobId})` : "";
        return [`open_record: ${record.kind} ${record.id}${jobNote}${suffix}`];
    }
    const list = parseOpenList(pathname, search);
    if (list) {
        return [`open_list: ${list.kind}${list.jobId ? ` (job: ${list.jobId})` : ""}`];
    }
    return [];
}
